import { Component, EventEmitter, OnInit, Output } from '@angular/core'
import { forkJoin, of } from 'rxjs'

import { BatchPaymentRow, PaymentBatchStateService } from '@modules/private/payment/data/services/view/payment-batch-state.service'
import { DistributionResult } from '@modules/private/payment/components/lease-distribution-selector/lease-distribution-selector.component'
import { toAllocatableLeases } from '@modules/private/payment/components/lease-distribution-selector/lease-allocation.util'
import { ApiLeaseService } from '@modules/private/lease/data/services/api/api-lease.service'

/**
 * Paso 4 · Los destinos — **solo las excepciones**.
 *
 * A qué arrendamiento va cada pago. Un cliente con un solo contrato activo se resuelve solo; uno con
 * varios exige que alguien elija y en qué orden (D3, D7). **Aquí solo se pintan los segundos**, más
 * los pagos cuyo cliente no tiene ningún arrendamiento activo, que hay que sacar del lote a mano.
 *
 * Cuando no hay ninguna excepción este paso **no existe**: la tira lo omite y el asistente pasa del
 * registro al impacto. No faltaba lógica de aplicación automática — sobraba ceremonia.
 *
 * **Los arrendamientos con sus letras se piden solo para los clientes que hay que decidir.** Con
 * cuarenta filas de las que treinta tienen un contrato único, pedirlos todos serían treinta consultas
 * caras para no enseñar nada: la capacidad de un arrendamiento se deriva de su tabla de amortización,
 * así que traerla no es gratis.
 */
@Component({
	selector: 'app-step4-destinos',
	templateUrl: './step4-destinos.component.html',
	styleUrls: ['./step4-destinos.component.scss'],
})
export class Step4DestinosComponent implements OnInit {
	@Output() onDone = new EventEmitter<void>()
	@Output() onBack = new EventEmitter<void>()
	/** Cerrar el asistente desde la barra de acciones, no solo desde la X de la cabecera. */
	@Output() onExit = new EventEmitter<void>()

	loading = false
	/** Qué pago tiene su selector abierto. Uno a la vez: el selector es alto y se compara mal en paralelo. */
	abierto = ''

	constructor(
		public readonly state: PaymentBatchStateService,
		private readonly apiLease: ApiLeaseService
	) {}

	ngOnInit(): void {
		this.loadLeases()
	}

	/**
	 * Solo lo que hay que decidir: cliente con varios arrendamientos, o con ninguno.
	 *
	 * Antes se listaban las cuarenta filas. Treinta de ellas no tenían nada que decidir —un solo
	 * contrato activo lo resuelve el motor (D3)— y obligaban a recorrer la lista con la vista para
	 * encontrar las tres que sí. Las resueltas se cuentan arriba, no se esconden.
	 */
	get rows(): BatchPaymentRow[] {
		return this.state.exceptionRows
	}

	get pendientes(): number {
		return this.state.applicableRows.filter((r) => !this.state.isDecided(r)).length
	}

	get puedeSeguir(): boolean {
		return !this.loading && this.state.decidedRows.length > 0 && this.pendientes === 0
	}

	/** Cuántos destinos tiene este pago que decidir. Cero es un problema, uno se resuelve solo. */
	necesitaElegir(row: BatchPaymentRow): boolean {
		return row.activeLeases.length > 1
	}

	alternar(row: BatchPaymentRow): void {
		this.abierto = this.abierto === row.paymentId ? '' : row.paymentId
	}

	onDistributionChange(row: BatchPaymentRow, result: DistributionResult): void {
		this.state.setDistribution(row, result)
	}

	/** Sacar un pago del lote lo deja registrado y sin aplicar: se aplica después, a mano. */
	alternarExclusion(row: BatchPaymentRow): void {
		row.excluded = !row.excluded
		if (row.excluded) this.state.invalidatePreview(row)
	}

	/** El destino resuelto de un pago, en una línea. */
	destino(row: BatchPaymentRow): string {
		if (!row.activeLeases.length) return 'El cliente no tiene ningún arrendamiento activo'
		if (row.activeLeases.length === 1) return row.activeLeases[0].folio
		if (!row.distribution || row.distribution.needsConfirmation) return `${row.activeLeases.length} arrendamientos — falta elegir`

		const destinos = row.distribution.allocations.filter((a) => a.amount > 0)
		const modo = row.distribution.distribution === 'CASCADE' ? 'en cascada' : 'en partes iguales'

		return `${destinos.map((a) => a.folio).join(' · ')} (${modo})`
	}

	/**
	 * Los arrendamientos de cada cliente, con sus letras, para poder repartir.
	 *
	 * Una llamada por CLIENTE con varios contratos, no una por pago: dos pagos del mismo cliente se
	 * reparten sobre los mismos arrendamientos.
	 */
	private loadLeases(): void {
		const conVarios = this.state.rows.filter((r) => r.activeLeases.length > 1)
		//* Un lote recuperado por su ruta no trae los arrendamientos de cada fila —los traía la lectura
		//* del archivo, que ya no está—, así que hay que pedirlos para todos.
		const sinDatos = this.state.rows.filter((r) => !r.activeLeases.length)
		const clientes = [...new Set([...conVarios, ...sinDatos].map((r) => r.clientId).filter(Boolean))]

		if (!clientes.length) return

		this.loading = true
		forkJoin(clientes.map((id) => this.apiLease.getActiveByClient(id).pipe())).subscribe({
			next: (respuestas) => {
				this.loading = false
				clientes.forEach((clientId, i) => {
					const allocatable = toAllocatableLeases(respuestas[i] ?? [], this.state.moraEnabled)
					for (const row of this.state.rows.filter((r) => r.clientId === clientId)) {
						row.allocatable = allocatable
						//* Recuperando un lote, la lista corta se rellena desde la larga: es la que decide si
						//* hay algo que elegir.
						if (!row.activeLeases.length) row.activeLeases = allocatable.map((l) => ({ leaseId: l.leaseId, folio: l.folio }))
					}
				})
			},
			error: () => {
				this.loading = false
			},
		})
	}
}
